/** Mock DTE playbook context. Not an automatic selector and not a DTE rule
 * driven by EMA crossovers.
 */
import { dteBand, earningsConflict, POLICY_WINDOW } from "@/workspace/mockTickerData";
import { technicalEntryContext } from "@/workspace/mockTickerData";
import { CachedBadge, KeyRow, MockBadge, WorkspacePanel } from "@/workspace/primitives";
import { LIVE_LABEL } from "@/workspace/live/workspaceLiveUnavailable";

const POLICY = {
  short: {
    name: "Short-duration",
    note: "Only as explicitly selected policy; higher gamma sensitivity near expiry remains visible.",
  },
  target: {
    name: "Standard premium income",
    note: "MarkovLab standard premium-income policy (35–45 DTE).",
  },
  long: {
    name: "Longer-duration income / assignment-aware",
    note: "60–75 DTE is the assignment-aware longer-duration band. This expiry is outside the 35–45 standard window.",
  },
};

export function DtePlaybookContext({ ticker, dte }) {
  const live = Boolean(ticker.live);
  const ctx = live ? null : technicalEntryContext(ticker.ticker);
  const conflict = earningsConflict(ticker.earnings, dte);
  const band = dteBand(dte);
  const policy = POLICY[band] || POLICY.target;
  const earnings = conflict.label;
  const premium = live
    ? (ticker.premium?.label || "Premium context unavailable")
    : (ticker.premium?.ivContext || "typical");
  const tech = live
    ? LIVE_LABEL.notEvaluated
    : ctx?.tag === "Mean-reversion context"
      ? "mean-reversion"
      : ctx?.tag === "Trend-aligned"
        ? "trend-aligned"
        : "mixed";

  return (
    <WorkspacePanel
      title="DTE playbook context"
      subtitle={live ? "Requested analysis tenor · not a current option contract" : "Display only — not an automatic selector"}
      notice={live ? <CachedBadge /> : <MockBadge />}
      hint={live
        ? "URL expiry/DTE is a page tenor only. Historical research uses a Canonical 30-DTE simulation."
        : "A 9/20 EMA crossover does not determine DTE. Later product context may include liquidity, policy, IV, earnings, regime, technicals, capital and quote quality."}
      testid="dte-playbook"
    >
      <div className="space-y-1.5 text-[11px]" style={{ color: "var(--hoip-text-secondary)" }}>
        <div>
          <span className="font-mono-data">Standard premium income:</span> {POLICY_WINDOW.min}–{POLICY_WINDOW.max} DTE
        </div>
        <div>
          <span className="font-mono-data">Longer-duration income / assignment-aware:</span> 60–75 DTE
        </div>
        <div>
          <span className="font-mono-data">Short-duration:</span> only as explicitly selected policy; higher gamma sensitivity near expiry must remain visible.
        </div>
      </div>
      <div className="mt-2.5 pt-2 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
        <KeyRow label={live ? "Requested analysis tenor" : "Selected DTE"}>{dte ?? "—"}</KeyRow>
        {live ? <KeyRow label="Historical research model">Canonical 30-DTE simulation</KeyRow> : null}
        <KeyRow label="Policy">{policy.name}</KeyRow>
        <KeyRow label="Earnings">{earnings}</KeyRow>
        <KeyRow label="Premium">{premium}</KeyRow>
        <KeyRow label="Technical context">{tech}</KeyRow>
      </div>
      <p className="text-[10px] mt-2 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
        {live
          ? "URL expiry/DTE is a page tenor only. Historical research is a Canonical 30-DTE simulation and is not based on the selected expiry. This playbook does not select a current option contract."
          : `${policy.note} Technical context is not a DTE rule.`}
      </p>
    </WorkspacePanel>
  );
}
